import React from 'react'
import { graphql, useFragment } from 'react-relay'

import { YStack, XStack } from 'tamagui'
import {
  Grid,
  Image,
  H1,
  Card,
  FormattedMarkdown,
  SponsorsSection,
  Paragraph,
} from '@newComponents'

import { EventScreenSettingsButton } from './EventScreenSettingsButton'
import { EventScreenNewCoverPhoto } from './EventScreenNewCoverPhoto'
import { EventScreenDescription } from './EventScreenDescription'
import { EventScreenOwner } from './EventScreenOwner'
import { EventScreenDateTime } from './EventScreenDateTime'
import { EventScreenJoin } from './EventScreenJoin'
import { EventScreenAttendCard } from './EventScreenAttendCard'
import { EventScreenMap } from './EventScreenMap'
import { EventScreenPresentations } from './EventScreenPresentations'
import { EventHostInvitationBanner } from './EventHostInvitationBanner'

import { EventScreenNewDesktop_event$key } from './__generated__/EventScreenNewDesktop_event.graphql'
import { EventScreenNewDesktop_query$key } from './__generated__/EventScreenNewDesktop_query.graphql'

export const EventScreenNewDesktop = ({
  event,
  query,
}: {
  event: EventScreenNewDesktop_event$key
  query: EventScreenNewDesktop_query$key
}) => {
  const data = useFragment(
    graphql`
      fragment EventScreenNewDesktop_event on Event {
        name
        description

        prettyUrl

        startAt
        endAt

        hasVenue
        hasExternalUrl

        seoVenue {
          address {
            city
            stateProvinceRegionCode
            zipPostalCode
            countryCode
          }
        }

        owner {
          __typename
          ... on Guild {
            sponsorshipDisplay
          }
        }

        ...EventScreenNewCoverPhoto_event
        ...EventScreenDescription_event
        ...EventScreenDateTime_event
        ...EventScreenOwner_event
        ...EventScreenSettingsButton_event
        ...EventScreenJoin_event
        ...EventScreenPresentations_event
        ...EventScreenAttendCard_event
        ...EventScreenMap_event
        ...EventHostInvitationBanner_event
      }
    `,
    event
  )

  const queryData = useFragment(
    graphql`
      fragment EventScreenNewDesktop_query on Query {
        ...SponsorsSection_query
        ...EventScreenAttendCard_query
        ...EventScreenJoin_query
        ...EventHostInvitationBanner_query
      }
    `,
    query
  )

  return (
    <Grid gap='$8' display='none' $gtSm={{ display: 'flex' }}>
      <Grid.Column>
        <YStack gap='$8' $gtLgHt={{ position: 'sticky', top: 96 }}>
          <EventScreenNewCoverPhoto event={data} />

          <EventScreenSettingsButton event={data} />

          <EventScreenAttendCard event={data} query={queryData} />

          {data.owner?.__typename !== 'Guild' ||
          data.owner.sponsorshipDisplay ? (
            <SponsorsSection query={queryData} padding='$4' />
          ) : null}
        </YStack>
      </Grid.Column>

      <Grid.Column flex={2}>
        <YStack gap='$8'>
          <EventHostInvitationBanner event={data} query={queryData} />

          <YStack gap='$6'>
            <H1 size='$8' paddingLeft='$5'>
              {data.name}
            </H1>

            <Grid gap='$8' alignItems='center'>
              <Grid.Column>
                <YStack gap='$6' paddingLeft='$5' flex={1}>
                  <EventScreenOwner event={data} />

                  <EventScreenDateTime event={data} />

                  <EventScreenJoin event={data} query={queryData} />
                </YStack>
              </Grid.Column>

              {!!data.hasVenue && (
                <Grid.Column
                  alignItems='center'
                  display='none'
                  $gtMd={{ display: 'flex' }}
                >
                  <YStack flex={1}>
                    <EventScreenMap event={data} />
                  </YStack>
                </Grid.Column>
              )}
            </Grid>
          </YStack>

          <EventScreenDescription event={data} />

          <EventScreenPresentations event={data} />

          {!!data.hasVenue && (
            <YStack $gtMd={{ display: 'none' }}>
              <EventScreenMap event={data} />
            </YStack>
          )}
        </YStack>
      </Grid.Column>
    </Grid>
  )
}
